import { LuCircleUser, LuGraduationCap, LuUser } from "react-icons/lu";
import { Box, Icon } from "@chakra-ui/react";

/**
 * The role a person holds in a unit, as one icon.
 *
 * Shared so the CeMMies list and the unit pages agree: the same person should
 * not be a different symbol depending on which page you came from.
 *
 * Head and deputy head come from the affiliation, not from the job title -- one
 * person heads two facilities, one unit has two heads, and a PI holds the title
 * "Principal Investigator" on a second affiliation they do not head. Student is
 * a programme enrollment, which is independent of both.
 */
export type MemberRole = "head" | "deputyHead" | "student" | "member";

const ROLES: Record<MemberRole, { icon: typeof LuUser; title: string }> = {
  head: { icon: LuCircleUser, title: "Head" },
  // The same icon as a head: both lead the unit, and the label beside it says
  // which.
  deputyHead: { icon: LuCircleUser, title: "Deputy head" },
  student: { icon: LuGraduationCap, title: "PhD student" },
  member: { icon: LuUser, title: "Member" },
};

export function resolveMemberRole({
  headRole,
  isStudent,
}: {
  headRole: "head" | "deputyHead" | null;
  isStudent: boolean;
}): MemberRole {
  if (headRole === "head") {
    return "head";
  }
  if (headRole === "deputyHead") {
    return "deputyHead";
  }
  return isStudent ? "student" : "member";
}

export function RoleIcon({ role, boxSize = 4 }: { role: MemberRole; boxSize?: number }) {
  const { icon, title } = ROLES[role];
  return (
    // title and aria-label both carry the role: the icon is the only indication
    // of it once the label is folded into the position column.
    <Box as="span" display="inline-flex" title={title} aria-label={title}>
      <Icon as={icon} boxSize={boxSize} />
    </Box>
  );
}

export function roleTitle(role: MemberRole): string {
  return ROLES[role].title;
}
